import {HttpClient, HttpParams} from "@angular/common/http";
import {Injectable} from "@angular/core";
import {AuthConfig, OAuthService} from "angular-oauth2-oidc";
import {Router} from "@angular/router";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {environment} from "src/environments/environment";
import { firstValueFrom, Subject } from "rxjs";
import {Location} from "@angular/common";
import {AppStateService} from "src/app/core-module/services/app-state.service";
import { UiSignalsStateService } from "./state/ui-signals-state.service";


@Injectable({
  providedIn: "root",
})
export class CustomAuthService {
  constructor(
    
    private oauthSrv: OAuthService,
    private httpSrv: HttpClient,
    private modalService: NgbModal,
    private router: Router,
    private location: Location,
    private appState: AppStateService,
    private uiSignals: UiSignalsStateService
  ) {
    window.addEventListener("storage", (event) => {
      if (event.key === "logout") {
        this.handleDataOnUserSessionEnd();
      }
    });
  }

  private token = null;
  private refreshToken = null;
  private expireTime = null;
  private tokenExpiryCheckInterval;
  private userInfo = null;
  private loggigIn = false;
  private idToken = null;

  public isRefreshing = false;
  public baseUrl = "";

  public onRefreshing = new Subject<boolean>();
  public onLogin = new Subject<boolean>();
  public onLogout = new Subject<boolean>();

  setIdToken(token) {
    this.idToken = token;
  }

  getIdToken() {
    return this.idToken;
  }

  setToken(token) {
    this.token = token;
  }

  getToken() {
    return this.token;
  }

  setRefreshToken(token) {
    this.refreshToken = token;
  }

  getRefreshToken() {
    return this.refreshToken;
  }

  setExpireTime(time) {
    this.expireTime = time;
  }

  getExpireTime() {
    return this.expireTime;
  }

  setUserInfo(info) {
    this.userInfo = info;
  }

  getUserInfo() {
    return this.userInfo;
  }

  getUserName() {
    if (this.userInfo) {
      return this.userInfo["first_name"] + " " + this.userInfo["last_name"];
    }
    return null;
  }

  configureOauth() {
    const authDomain = environment.oauth.authoriserDomain;
    const issuerUrl = `https://${authDomain}.accounts.ondemand.com`;
    const config: AuthConfig = {
      issuer: issuerUrl,
      clientId: environment.oauth.clientId,
      redirectUri: `${window.location.protocol}//${window.location.host}/home`,
      scope: "openid",
      responseType: "code",
      showDebugInformation: true,
      loginUrl: `${issuerUrl}/oauth2/authorize`,
    };
    this.oauthSrv.configure(config);
  }

  checkForAutoLogin() {
    if (window.location.href.includes('fromMyLighting=true')) {
      this.login();
      return;
    }
    if (this.token) {
      return;
    }
    const token = this.getToken();
    if (token) {
      const refreshToken = this.getRefreshToken();
      const expireTime = this.appState.getExpiresIn();
      const userInfo = this.getUserInfo();
      if (!userInfo) {
        this.getUserInfoUsingCode();
      }
      const idToken = this.getIdToken();
      this.setToken(token);
      this.setRefreshToken(refreshToken);
      this.setExpireTime(expireTime ? JSON.parse(expireTime) : null);
      this.setUserInfo(userInfo ? JSON.parse(userInfo) : null);
      this.setIdToken(idToken);
      this.startTokenExpirationCheck();
    }
  }

  login() {
    this.oauthSrv.initCodeFlow();
  }

  async startTokenGenUsingCode(code) {
    this.setLoggingIn(true);
    await this.getTokenOnLogIn(code);
    this.getUserInfoUsingCode();
    this.setLoggingIn(false);
    this.location.replaceState("/");
    this.router.navigate(["/"], {replaceUrl: true});
  }

  async getTokenOnLogIn(code) {
    const codeVerifier = sessionStorage.getItem("PKCE_verifier");
    const tokenUrl = this.baseUrl + "auth/api/v1/authenticate/code";
    let data;
    try {
      data = await firstValueFrom(
        this.httpSrv.post(tokenUrl, {
          code: code,
          code_verifier: codeVerifier,
          redirect_uri: `${window.location.protocol}//${window.location.host}/home`,
        })
      );
    } catch {
      sessionStorage.removeItem("redirectUrl");
      return;
    }

    if (data) {
      this.setToken(data["access_token"]);
      this.setRefreshToken(data["refresh_token"]);
      this.setIdToken(data["id_token"]);
      this.setExpireTime(Date.now() + data["expires_in"] * 1000);
      this.startTokenExpirationCheck();
      this.onLogin.next(true);
      sessionStorage.setItem("isLoggedIn", "true");
    }
  }

  getTokenUsingRefreshToken() {
    const refreshurl = this.baseUrl + "auth/api/v1/authenticate/refresh";
    this.httpSrv
      .post(refreshurl, {
        refresh_token: this.refreshToken,
      })
      .subscribe({
        next: (data) => {
          this.setToken(data["access_token"]);
          this.setRefreshToken(data["refresh_token"]);
          this.setIdToken(data["id_token"]);
          this.setExpireTime(Date.now() + data["expires_in"] * 1000);
          this.getUserInfoUsingCode();
          this.isRefreshing = false;
          this.onRefreshing.next(true);
        },
        error: () => {
          this.isRefreshing = false;
          this.onRefreshing.next(true);
        }
      });
  }

  async getUserInfoUsingCode() {
    const url = `https://${environment.oauth.authoriserDomain}.accounts.ondemand.com/oauth2/userinfo`;
    try {
      const data = await firstValueFrom(this.httpSrv.get(url));
      if (data) {
        this.userInfo = data;
        this.expireTime = data["exp"] * 1000;
        this.appState.setExpiresIn(this.expireTime);
      }
    } catch {
    }
  }

  startTokenExpirationCheck() {
    this.tokenExpiryCheckInterval = setInterval(() => {
      this.tokenCheck();
    }, 60000);
    this.tokenCheck();
  }

  tokenCheck() {
    if (this.isRefreshing) return;
    if (this.expireTime && this.expireTime < Date.now()) {
      if (document.visibilityState == "visible") this.handleTokenExpiry();
    } else if (this.expireTime && this.expireTime - Date.now() <= 180000) {
      if (document.visibilityState == "visible") {
        this.isRefreshing = true;
        this.getTokenUsingRefreshToken();
      }
    }
  }

  handleTokenExpiry(fromLogout = false) {
    if (fromLogout) this.handleDataOnUserSessionEnd();
    const logourUrl = `https://${environment.oauth.authoriserDomain}.accounts.ondemand.com/oauth2/logout`;
    const redirectUri = `${window.location.protocol}//${window.location.host}/home`;
    let redirectUrl = redirectUri + "?logout=true";
    if (redirectUrl.includes("localhost")) {
      redirectUrl =
        "https://dev.multione.signify.com/home?redirectToLocal=" +
        redirectUri;
    }
    this.location.replaceState("/");
    this.onLogout.next(true);
    this.clearTokenCHeck();
    window.location.href = `${logourUrl}?client_id=${environment.oauth.clientId}&id_token_hint=${
      this.idToken
    }&post_logout_redirect_uri=${redirectUrl}`;
  }

  /**Revoke the token if authoriser does not handle the token revocation implicitly */
  // async revokeToken() {
  //   const revoke_url = "https://alhnxkfca.accounts.ondemand.com/oauth2/revoke";
  //   if (this.token) {
  //     const body = new URLSearchParams();
  //     body.set("token", this.token);
  //     body.set("token_type_hint", "access_token");
  //     body.set("client_id", this.clientId);

  //     const headers = {
  //       "content-Type": "application/x-www-form-urlencoded",
  //     };

  //     await this.httpSrv
  //       .post(revoke_url, body.toString(), { headers })
  //       .toPromise();
  //   }
  // }

  clearTokenCHeck() {
    if (this.tokenExpiryCheckInterval) {
      clearInterval(this.tokenExpiryCheckInterval);
    }
  }

  setLoggingIn(value) {
    this.loggigIn = value;
  }

  IsLoggigIn() {
    return this.loggigIn;
  }

  getHostName() {
    const host = window.location.hostname;
    if (host.includes("localhost")) return "localhost";
    if (host.includes("tst")) return "tst";
    if (host.includes("dev")) return "dev";
    if (host.includes("staging")) return "staging";
    if (host.includes("www.multione.signify.com")) return "prod";
  }

  isTokenValid() {
    return !!(this.token && this.expireTime >= Date.now());
  }

  isUserLoggedIn() {
    return !!(this.token && this.isTokenValid());
  }

  isGetNewToken() {
    if (!this.isUserLoggedIn() && sessionStorage.getItem('isLoggedIn') == 'true')
      return true;
  }

  async handleDataOnUserSessionEnd() {
    this.token = null;
    this.expireTime = null;
    this.refreshToken = null;
    this.userInfo = null;
    this.appState.setLogout(Date.now().toString());
    this.appState.removeExpiresIn();
    this.appState.setReaderDevices(null);
    this.appState.setReaderDeviceFullInfo(null);
    this.appState.setModes(null);
    this.appState.removeStoreColumn();
    this.appState.removeLastSorting();
    this.appState.setStoreSaveFeatureFile(null);
    this.appState.setStoreDCFFile(null);
    sessionStorage.removeItem('isLoggedIn');
    this.modalService.dismissAll();
    this.uiSignals.sendCloseSelectDevicePopup('close');
    this.uiSignals.sendCloseConfigPopup('close');
    this.router.navigate(['/home']);
  }
}
